import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { isNil } from "lodash";
import { useCallback, useEffect, useState } from "react";
import type { Job, JobState } from "../../types/queue-types";
import { getQueueJobs, performQueueAction } from "../../utils/queue-api";
import JobDetailModal from "./job-detail-modal";

type JobListProps = {
  readonly queueName: string;
  readonly onBack: () => void;
  readonly onRefresh: () => void;
};

const JOB_STATES: JobState[] = [
  "failed",
  "waiting",
  "active",
  "delayed",
  "completed",
];

const DEFAULT_LIMIT = 25;

const formatTimestamp = (timestamp: number | undefined): string => {
  if (isNil(timestamp)) {
    return "-";
  }
  return new Date(timestamp).toLocaleString();
};

const formatDuration = (job: Job): string => {
  if (isNil(job.processedOn) || isNil(job.finishedOn)) {
    return "-";
  }
  const durationMs = job.finishedOn - job.processedOn;
  if (durationMs < 1000) {
    return `${durationMs}ms`;
  }
  return `${(durationMs / 1000).toFixed(2)}s`;
};

const JobList = ({ queueName, onBack, onRefresh }: JobListProps) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedState, setSelectedState] = useState<JobState>("failed");
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getQueueJobs(
        queueName,
        selectedState,
        limit,
        page * limit,
      );
      setJobs(response.jobs);
      setTotal(response.total);
      setError(null);
    } catch (error_) {
      const errorMessage =
        error_ instanceof Error ? error_.message : "Failed to fetch jobs";
      setError(errorMessage);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  }, [queueName, selectedState, page, limit]);

  useEffect(() => {
    void fetchJobs();
  }, [fetchJobs]);

  const handleRetry = useCallback(
    async (jobId: string) => {
      try {
        await performQueueAction("retry", queueName, jobId);
        await fetchJobs();
        onRefresh();
      } catch (error_) {
        const errorMessage =
          error_ instanceof Error ? error_.message : "Failed to retry job";
        setError(errorMessage);
        setSnackbarOpen(true);
      }
    },
    [queueName, fetchJobs, onRefresh],
  );

  const handleRemove = useCallback(
    async (jobId: string) => {
      try {
        await performQueueAction("remove", queueName, jobId);
        await fetchJobs();
        onRefresh();
        setModalOpen(false);
        setSelectedJob(null);
      } catch (error_) {
        const errorMessage =
          error_ instanceof Error ? error_.message : "Failed to remove job";
        setError(errorMessage);
        setSnackbarOpen(true);
      }
    },
    [queueName, fetchJobs, onRefresh],
  );

  const handleStateChange = (
    _event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setSelectedState(JOB_STATES[newValue] ?? "failed");
    setPage(0);
  };

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setLimit(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (job: Job) => {
    setSelectedJob(job);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedJob(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
        }}
      >
        <Tooltip title="Back to Queues">
          <IconButton onClick={onBack}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h6">{queueName}</Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh">
          <IconButton
            disabled={loading}
            onClick={() => {
              void fetchJobs();
            }}
          >
            {loading ? <CircularProgress size={24} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <Tabs
        sx={{ mb: 2 }}
        value={JOB_STATES.indexOf(selectedState)}
        onChange={handleStateChange}
      >
        {JOB_STATES.map((state) => (
          <Tab
            key={state}
            label={state.charAt(0).toUpperCase() + state.slice(1)}
          />
        ))}
      </Tabs>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Job ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell align="center">Attempts</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <TableRow
                key={job.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  handleRowClick(job);
                }}
              >
                <TableCell>{job.id}</TableCell>
                <TableCell>{job.name}</TableCell>
                <TableCell align="center">{job.attemptsMade}</TableCell>
                <TableCell>{formatTimestamp(job.timestamp)}</TableCell>
                <TableCell>{formatDuration(job)}</TableCell>
                <TableCell align="center">
                  {selectedState === "failed" && (
                    <Tooltip title="Retry">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleRetry(job.id);
                        }}
                      >
                        <ReplayIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleRemove(job.id);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {jobs.length === 0 && !loading && (
              <TableRow>
                <TableCell align="center" colSpan={6}>
                  <Typography color="text.secondary">
                    No {selectedState} jobs
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={limit}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <JobDetailModal
        isFailed={selectedState === "failed"}
        job={selectedJob}
        open={modalOpen}
        onClose={handleCloseModal}
        onRemove={handleRemove}
        onRetry={handleRetry}
      />

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        autoHideDuration={6000}
        open={snackbarOpen}
        onClose={handleCloseSnackbar}
      >
        <Alert
          severity="error"
          sx={{ width: "100%" }}
          onClose={handleCloseSnackbar}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default JobList;
